<template>
  <div id="hls-video-player">
    <p v-show="cameraMsg">{{ cameraMsg }}</p>
    <video-player
      class="media-video-player card-video vjs-big-play-centered"
      ref="videoPlayer"
      :options="playerOptions"
      @ready="onPlayerReadied"
      @error="onPlayerError"
    />
  </div>
</template>

<script>
import { videoPlayer } from "vue-videojs7";
import moment from "moment-timezone";
import { AiseaApiFactory } from "@/api/aisea/apiFactory";

/**
 * 航海記録再生用のカメラ(HLS)プレイヤー。
 *
 * GKMG のカメラ配信は「1時間単位で分割された OnDemand.m3u8(VOD)」方式のため、
 * aisea の onDemandByHash(時間範囲でm3u8本文を返すAPI)とは互換が無い。
 * ここでは GKMG の他画面(CameraView / PlayBackPlayerHls)と同じ作法で再生する:
 *   1. connectHLS(camera.id) で HLS のベースURL(末尾 HLS.m3u8) を取得
 *   2. 再生時刻の {YYYYMMDD}/{HH}/OnDemand.m3u8 に置換して src に設定
 *   3. タイムライン(current)に追従し、時をまたいだら src を切り替える
 *
 * [NOTE] カメラ配信バックエンド依存が強く、実機での再生検証が必須。
 */
export default {
  components: { videoPlayer },
  props: {
    camera: { type: Object, required: true },
    current: { type: Number, required: false },
    start: { type: Number, required: false },
    end: { type: Number, required: false },
    fps: { type: Number, required: false },
    isPause: { type: Boolean, required: false },
    width: { type: Number, required: false }
  },
  data() {
    return {
      connectedUrl: null, // connectHLS が返すベースURL(末尾 HLS.m3u8)
      currentHourKey: null, // 現在読み込み中の YYYYMMDD/HH
      cameraMsg: "接続中...",
      // 大きなズレのみ補正(毎秒シークすると再生がガタつくため)
      driftThresholdSec: 2,
      playerOptions: {
        playbackRates: [1, 2, 4, 8, 16],
        fluid: this.width ? false : true,
        width: this.width,
        autoplay: false,
        muted: true,
        controls: false,
        preload: "auto",
        fullscreen: false,
        bigPlayButton: false,
        flash: { vhs: { withCredentials: false } },
        html5: { vhs: { withCredentials: false } },
        language: "ja",
        languages: {
          ja: {
            "The media could not be loaded, either because the server or network failed or because the format is not supported.":
              "映像がありません"
          }
        },
        controlBar: { pictureInPictureToggle: false }
      }
    };
  },
  computed: {
    player() {
      if (typeof this.$refs.videoPlayer === "undefined") return null;
      return this.$refs.videoPlayer.player;
    }
  },
  watch: {
    current(value) {
      this.syncVideo(value);
    },
    fps(value) {
      this.applyPlaybackRate(value);
    },
    isPause(value) {
      if (this.player) {
        value ? this.player.pause() : this.player.play();
      }
    }
  },
  async mounted() {
    await this.connect();
  },
  methods: {
    async connect() {
      try {
        const res = await AiseaApiFactory.get("enterpriseCamera").connectHLS(
          this.camera.id
        );
        this.connectedUrl = res && res.data ? res.data.url : null;
      } catch (e) {
        this.connectedUrl = null;
      }

      if (!this.connectedUrl) {
        this.cameraMsg = "映像がありません";
        this.$emit("isCameraReady", false);
        return;
      }

      this.cameraMsg = "";
      this.$emit("isCameraReady", true);
      this.loadHour(this.current, true);
    },

    hourKey(epoch) {
      return moment.unix(epoch).utc().format("YYYYMMDD/HH");
    },
    hourStartUnix(epoch) {
      return moment.unix(epoch).utc().startOf("hour").unix();
    },
    buildOnDemandSrc(epoch) {
      if (!this.connectedUrl) return null;
      return this.connectedUrl.replace(
        /HLS\.m3u8$/i,
        `${this.hourKey(epoch)}/OnDemand.m3u8`
      );
    },

    // 対象時刻の時間(HH)のOnDemandを読み込み、その時間内の位置へシーク
    loadHour(epoch, force = false) {
      if (!this.player || !this.connectedUrl || !epoch) return;
      const key = this.hourKey(epoch);
      if (!force && key === this.currentHourKey) return;
      this.currentHourKey = key;

      const src = this.buildOnDemandSrc(epoch);
      if (!src) return;
      this.player.src({
        withCredentials: false,
        type: "application/x-mpegurl",
        src: src
      });
      this.player.one("loadedmetadata", () => {
        this.seekWithinHour(epoch);
        if (!this.isPause) this.player.play();
        this.applyPlaybackRate(this.fps);
      });
    },

    seekWithinHour(epoch) {
      if (!this.player) return;
      this.player.currentTime(epoch - this.hourStartUnix(epoch));
    },

    // HTMLMediaElement の playbackRate は最大16倍まで。x32 は videojs 側で
    // NotSupportedError になるため、16 を超える倍率は適用しない(タイムラインは32倍で進む)。
    applyPlaybackRate(rate) {
      if (!this.player) return;
      if (!rate || rate > 16) return;
      this.player.playbackRate(rate);
    },

    // タイムラインの current に追従
    syncVideo(epoch) {
      if (!this.player || !this.connectedUrl || !epoch) return;

      // 時をまたいだら OnDemand を切り替える
      if (this.hourKey(epoch) !== this.currentHourKey) {
        this.loadHour(epoch, true);
        return;
      }

      // 同じ時間内は、大きくズレたときのみ補正(通常はネイティブ再生に任せる)
      const target = epoch - this.hourStartUnix(epoch);
      const actual = this.player.currentTime();
      if (Math.abs(actual - target) > this.driftThresholdSec) {
        this.player.currentTime(target);
      }
    },

    onPlayerReadied() {},
    onPlayerError() {
      this.cameraMsg = "映像がありません";
    }
  }
};
</script>

<style lang="scss" scoped>
#hls-video-player {
  position: relative;
  width: 300px;

  p {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    margin: 0;
    padding: 0;
    color: white;
    z-index: 5;
  }
}
</style>
